import type { IconType } from '@fshr/components';
import type { ProfileV1 } from '@fshr/microservices/profile';

export type ProfileSectionId = 'personal-info' | 'email-comm' | 'stay-pref' | 'interests';

export interface ProfileSection {
	id: ProfileSectionId;
	label: string;
	description: string;
	icon: IconType;
	href: string;
}

/** Shown under the Interests tab when the guest already has selected interests (Figma "Interests Selected"). */
export const INTERESTS_SELECTED_DESCRIPTION =
	'Share your interests so we can tailor your recommendations.';

/** Overview card order (matches Figma 2×2 grid: left-to-right, top-to-bottom). */
export const PROFILE_SECTIONS: ProfileSection[] = [
	{
		id: 'personal-info',
		label: 'Personal Information',
		description: 'Update your contact details and personal information.',
		icon: 'person',
		href: '/profile/account/info/',
	},
	{
		id: 'stay-pref',
		label: 'Stay Preferences',
		description: 'Tell us how you would prefer to experience your stay.',
		icon: 'beds',
		href: '/profile/account/preferences/',
	},
	{
		id: 'interests',
		label: 'Interests',
		// Curly apostrophe (U+2019) — existing dictionary key; do not replace with ASCII '
		// Empty / not-selected Interests tab: shown under "Select your interests" in AddInterestsView
		description: 'We’ll use your choices to make the content you see more personal.',
		icon: 'experience',
		href: '/profile/account/interests/',
	},
	{
		id: 'email-comm',
		label: 'Email Communications',
		description: 'Choose the emails you would like to receive.',
		icon: 'email',
		href: '/profile/account/subscriptions/',
	},
];

/** Existing Account & Preferences tab order (unchanged). */
export const PROFILE_ACCOUNT_TAB_IDS: ProfileSectionId[] = [
	'personal-info',
	'email-comm',
	'stay-pref',
	'interests',
];

export function getProfileSection(id: ProfileSectionId): ProfileSection {
	const section = PROFILE_SECTIONS.find(s => s.id === id);
	if (!section) {
		throw new Error(`Unknown profile section: ${id}`);
	}
	return section;
}

export function hasActiveInterests(profile: ProfileV1.ProfileResponse | null | undefined): boolean {
	const interests = profile?.interests;
	if (!interests) {
		return false;
	}

	const groups = [
		interests.foodAndDrink,
		interests.healthAndWellness,
		interests.travelAndLifestyle,
	];

	return groups.some(group =>
		Object.values(group || {}).some(
			(interest): interest is ProfileV1.Interest =>
				interest != null &&
				typeof interest === 'object' &&
				'active' in interest &&
				Boolean(interest.active)
		)
	);
}
